<template>
  <div class="agency-card-container">
    <v-card
      class="modern-agency-card rounded-xl"
      :elevation="isHovered ? 3 : 1"
      @mouseover="isHovered = true"
      @mouseleave="isHovered = false"
      @click="goToProfile"
      :style="onlyFollowed && !getFanned ? 'display: none' : ''"
    >
      <!-- Loading state -->
      <v-skeleton-loader
        v-if="loading || !agency"
        type="image, list-item-two-line"
        class="pa-4"
      />
      <!-- Loaded state -->
      <template v-else>
        <!-- Card header with background and avatar -->
        <div class="card-header position-relative">
          <div class="header-bg">
            <img
              v-if="agency.bgImage"
              :src="agency.bgImage"
              alt="Agency Cover Image"
              class="w-100 h-100"
            />
          </div>
          <div class="avatar-container">
            <agency-avatar
              :img="agency.midThumbnail || agency.image"
              :name="agency.name"
              :size="avatarSize"
              class="elevation-3"
            />
          </div>
        </div>

        <!-- Card content -->
        <div class="mt-2 text-center pa-4">
          <h3 class="mb-1 text-h6 font-weight-bold primary-color text-truncate">
            {{ agency.name }}
          </h3>

          <div
            class="justify-center mb-4 d-flex align-center"
            v-if="this.profile.type === 'Agency'"
          >
            <v-icon small color="grey darken-1" class="mr-1"
              >mdi-account-group</v-icon
            >
            <span class="text-caption grey--text text--darken-1">
              {{ agency.followers?.length || 0 }}
              {{
                (agency.followers?.length || 0) > 1 ? "followers" : "follower"
              }}
            </span>
          </div>
          <div
            class="justify-center mb-4 d-flex align-center"
            v-if="this.profile.type === 'Volunteer'"
          >
            <v-icon small color="grey darken-1" class="mr-1"
              >mdi-account-group</v-icon
            >
            <span class="text-caption grey--text text--darken-1">
              {{ agency.fanned?.length || 0 }}
              {{ (agency.fanned?.length || 0) > 1 ? "fanned" : "fan" }}
            </span>
          </div>

          <div class="d-flex justify-space-between">
            <v-btn
              :color="getFanned ? 'success' : '#0A5B8A'"
              :outlined="getFanned"
              :dark="!getFanned"
              rounded
              class="flex-grow-1"
              height="36"
              @click.stop="becameFan"
              :loading="loading"
            >
              <v-icon left size="18">{{
                getFanned ? "mdi-check" : "mdi-plus"
              }}</v-icon>
              {{ getFanned ? "Following" : "Follow" }}
            </v-btn>
          </div>
        </div>
      </template>
    </v-card>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import AgencyAvatar from "@/components/agency/AgencyAvatar.vue";

export default {
  components: { AgencyAvatar },
  props: {
    agency: Object
  },
  data() {
    return {
      loading: false,
      onlyFollowed: false,
      isHovered: false
    };
  },
  computed: {
    ...mapState("auth", {
      profile: "profile",
      type: "type"
    }),
    getFanned() {
      if (!this.profile.fanned) return false;
      return this.profile.fanned.includes(this.agency._id);
    },
    getFollowed() {
      if (!this.profile.followed) return false;
      return this.profile.followed.includes(this.agency._id);
    },
    avatarSize() {
      const bp = this.$vuetify.breakpoint;
      if (bp.xs) return 50;
      if (bp.sm) return 60;
      return 70;
    }
  },
  methods: {
    ...mapActions("volunteer", {
      followAgency: "followAgency"
    }),
    goToProfile() {
      this.$router.push({
        name: "agency-profile-details",
        params: { id: this.agency._id }
      });
    },

    async becameFan() {
      this.loading = true;
      const wasFanned = this.getFanned;

      try {
        await this.followAgency({
          fanned: !wasFanned,
          agency: this.agency._id
        });

        // Update the local agency data immediately
        if (wasFanned) {
          // Remove from fanned list when unfollowing
          this.agency.fanned = this.agency.fanned.filter(
            id => id !== this.profile._id
          );
        } else {
          // Add to fanned list when following
          this.agency.fanned = [
            ...(this.agency.fanned || []),
            this.profile._id
          ];
        }
      } catch (error) {
        this.$store.dispatch("notification/show", {
          text: error.response?.data?.message || "An error occurred",
          color: "error"
        });
      } finally {
        this.loading = false;
      }
    }
  },
  watch: {
    "$route.query.agency": function(value) {
      this.onlyFollowed = value == "followed";
    }
  },
  mounted() {
    this.onlyFollowed = this.$route.query.agency == "followed";
  }
};
</script>

<style scoped>
.agency-card-container {
  width: 100%;
  max-width: 300px;
  margin: auto;
  padding: 0 8px;
}

.modern-agency-card {
  transition: all 0.3s ease;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.card-header {
  height: 100px;
  width: 100%;
  position: relative;
}

.header-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #0a5b8a 0%, #0a7b8a 100%);
}

.avatar-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, 0%);
  z-index: 2;
  border-radius: 50%;
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* Responsive Typography and Layout Adjustments */
@media (max-width: 1024px) {
  .modern-agency-card {
    padding: 12px;
  }

  .card-header {
    height: 90px;
  }
}

@media (max-width: 768px) {
  .agency-card-container {
    max-width: 90%;
  }

  .card-header {
    height: 80px;
  }
}

@media (max-width: 600px) {
  .agency-card-container {
    max-width: 100%;
    padding: 0 12px;
  }

  .card-header {
    height: 70px;
  }

  .avatar-container {
    top: 35px;
  }

  h3.text-h6 {
    font-size: 1rem;
  }

  .v-btn {
    font-size: 0.85rem;
  }
}

.v-btn {
  font-size: 13px;
}
.v-btn {
  font-size: 13px;
}
.agency-cards-wrapper {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  margin: 0 auto;
  padding: 16px;
}

.agency-card-container {
  flex: 1 1 calc(50% - 16px);
  max-width: 300px;
}

/* On small screens, force single column */
@media (max-width: 768px) {
  .agency-card-container {
    flex: 1 1 100%;
    max-width: 100%;
  }
}
</style>
